import { api } from "../config/api";
import { post } from "../api";

export interface CategoriesSessionArgs {
  business_id: string | null;
  device_token: string | null;

  session_id: string | null;
}

export interface CategoryServiceItem {
  name?: string;
  category_id?: number | string;
  shortcode?: string;
  description?: string;
  is_active?: boolean;
  is_live?: boolean;
  sla_days?: number | null;
  deadline?: string | null;
  deadline_reason?: string | null;
}

export interface CategoryTreeItem {
  category_id?: string | number;
  name: string;
  shortcode: string;
  description?: string;
  is_active?: boolean;
  is_live?: boolean;
  sub_category?: CategoryTreeItem[];
  services?: CategoryServiceItem[];
}

export interface GetAllCategoryInfoArgs extends CategoriesSessionArgs {
  category_type?: "all" | "category" | "sub_category" | "service";
}

export interface GetAllCategoryInfoResponse {
  success?: boolean;
  data?: {
    category_tree?: CategoryTreeItem[];
  };
  errors?: string;
}

export interface CreateCategoryPayload extends CategoriesSessionArgs {
  name: string;
  shortcode: string;
  description?: string;
  is_active?: boolean;
}

export interface CreateCategoryResponse {
  success?: boolean;
  data?: { category_tree?: CategoryTreeItem[] };
  errors?: string;
}

export interface UpdateCategoryPayload extends CategoriesSessionArgs {
  category_list: CategoryTreeItem[];
}

export interface UpdateCategoryResponse {
  success?: boolean;
  data?: { category_tree?: CategoryTreeItem[] };
  errors?: string;
}

export interface MakeCategoryLivePayload extends CategoriesSessionArgs {
  category_id: string | number;
  make_live?: boolean;
}

export interface MakeCategoryLiveResponse {
  success?: boolean;
  data?: { category_info?: { category_state?: boolean } };
  errors?: string | string[];
  error_code?: string | null;
  meta?: unknown;
}

export interface GetPlatformCategoriesPayload {
  category_type: string;
  device_token: string | null;
  session_id: string | null;
}

export interface PlatformCategoryListItem {
  category_id: number;
  name?: string;
  description?: string;
  shortcode?: string;
  is_active?: boolean;
  is_live?: boolean;
  other_doctype?: boolean;
}

export interface GetPlatformCategoriesResponse {
  success?: boolean;
  data?: { category_info?: PlatformCategoryListItem[] };
  errors?: string | string[];
  error_code?: string | null;
}

export interface ApplyPlatformTemplateNewPayload {
  business_id: number;
  category_id: string | number;
  device_token: string;
  session_id: string;
}

export interface ApplyPlatformTemplateNewData {
  business_id?: number;
  business_name?: string;
  category_id?: number;
  category_name?: string;
  category_not_exported?: string[];
  process_not_exported?: string[];
  category_success_list?: string[];
  process_success_list?: string[];
}

export interface ApplyPlatformTemplateNewResponse {
  success?: boolean;
  data?: ApplyPlatformTemplateNewData;
  errors?: string | string[] | null;
  error_code?: string | null;
  meta?: unknown;
}

export interface ValidateShortcodePayload extends CategoriesSessionArgs {
  shortcode: string;
  entity_type: "category" | "sub_category" | "groups" | "clients" | "process";
}

export interface ValidateShortcodeResponse {
  success?: boolean;
  data?: { is_valid?: boolean };
  errors?: string;
}

function getAllCategoryInfo(args: GetAllCategoryInfoArgs): Promise<GetAllCategoryInfoResponse> {
  const { business_id, device_token, session_id, category_type } = args;
  if (!device_token || !session_id) {
    return Promise.reject(new Error("device_token and session_id are required"));
  }

  if (!business_id) {
    return post<GetAllCategoryInfoResponse>(api.GET_ALL_PLATFORM_CATEGORY_INFO, {
      category_type: category_type ?? "all",
      device_token,
      session_id,
    });
  }

  return post<GetAllCategoryInfoResponse>(api.GET_ALL_CATEGORY_INFO, {
    business_id,
    device_token,
    session_id,
  });
}

function getPlatformCategories(
  payload: GetPlatformCategoriesPayload
): Promise<GetPlatformCategoriesResponse> {
  const { category_type, device_token, session_id } = payload;
  if (!device_token || !session_id) {
    return Promise.reject(new Error("device_token and session_id are required"));
  }
  return post<GetPlatformCategoriesResponse>(api.GET_PLATFORM_CATEGORIES, {
    category_type: String(category_type ?? "").trim(),
    device_token,
    session_id,
  });
}

function applyPlatformTemplateNew(
  payload: ApplyPlatformTemplateNewPayload
): Promise<ApplyPlatformTemplateNewResponse> {
  const { business_id, category_id, device_token, session_id } = payload;
  if (!device_token || !session_id) {
    return Promise.reject(new Error("device_token and session_id are required"));
  }
  if (business_id === undefined || business_id === null || !Number.isFinite(Number(business_id))) {
    return Promise.reject(new Error("business_id is required"));
  }
  const cat =
    category_id === undefined || category_id === null
      ? ""
      : typeof category_id === "number"
        ? String(category_id)
        : String(category_id).trim();
  if (!cat) {
    return Promise.reject(new Error("category_id is required"));
  }
  return post<ApplyPlatformTemplateNewResponse>(api.APPLY_TEMPLATE_NEW, {
    business_id: Number(business_id),
    category_id: cat,
    device_token,
    session_id,
  });
}

function createCategory(payload: CreateCategoryPayload): Promise<CreateCategoryResponse> {
  const { business_id, device_token, session_id } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject(new Error("business_id, device_token and session_id are required"));
  }
  return post<CreateCategoryResponse>(api.CREATE_CATEGORY, payload);
}

function makeCategoryLive(payload: MakeCategoryLivePayload): Promise<MakeCategoryLiveResponse> {
  const { business_id, device_token, session_id, category_id, make_live } = payload;
  if (!device_token || !session_id || category_id === undefined || category_id === null) {
    return Promise.reject(new Error("device_token, session_id and category_id are required"));
  }
  const categoryIdNum =
    typeof category_id === "number" ? category_id : Number(String(category_id).trim());
  if (!Number.isFinite(categoryIdNum)) {
    return Promise.reject(new Error("category_id must be a valid number"));
  }
  if (!business_id) {
    const goingLive = make_live !== false;
    return post<MakeCategoryLiveResponse>(api.MAKE_PLATFORM_CATEGORY_LIVE, {
      category_id: categoryIdNum,
      ...(!goingLive ? { category_state: false } : {}),
      device_token,
      session_id,
    });
  }
  const businessIdNum = Number(business_id);
  return post<MakeCategoryLiveResponse>(api.MAKE_CATEGORY_LIVE, {
    category_id: categoryIdNum,
    business_id: Number.isFinite(businessIdNum) ? businessIdNum : business_id,
    device_token,
    session_id,
  });
}

function sanitizeCategoryListForApi(list: CategoryTreeItem[]): Array<Record<string, unknown>> {
  return list.map((item) => {
    const id = item.category_id;
    const categoryId =
      id === undefined || id === null || id === "" ? "" : typeof id === "number" ? id : Number(id);
    const isNumericId = typeof categoryId === "number" && !Number.isNaN(categoryId);
    const node: Record<string, unknown> = {
      name: item.name ?? "",
      category_id: isNumericId ? categoryId : "",
      shortcode: item.shortcode ?? "",
      description: item.description ?? "",
      is_active: item.is_active ?? true,
      is_live: item.is_live ?? item.is_active ?? false,
      sub_category:
        (item.sub_category?.length ?? 0) > 0 ? sanitizeCategoryListForApi(item.sub_category) : [],
      services: sanitizeServicesForApi(item.services ?? []),
    };
    return node;
  });
}

function sanitizeServicesForApi(services: CategoryServiceItem[]): Array<Record<string, unknown>> {
  return services.map((s) => {
    const id = s.category_id;
    const categoryId =
      id === undefined || id === null || id === ""
        ? undefined
        : typeof id === "number"
          ? id
          : Number(id);
    const out: Record<string, unknown> = {
      category_id: categoryId !== undefined && !Number.isNaN(categoryId) ? categoryId : "",
      name: s.name,
      shortcode: s.shortcode,
      description: s.description,
      is_active: s.is_active ?? true,
      is_live: s.is_live ?? s.is_active ?? false,
    };
    if (s.sla_days !== undefined && s.sla_days !== null) out.sla_days = s.sla_days;
    if (s.deadline !== undefined && s.deadline !== null) out.deadline = s.deadline;
    if (s.deadline_reason !== undefined && s.deadline_reason !== null)
      out.deadline_reason = s.deadline_reason;
    return out;
  });
}

function updateCategory(payload: UpdateCategoryPayload): Promise<UpdateCategoryResponse> {
  const { business_id, device_token, session_id, category_list } = payload;
  if (!device_token || !session_id) {
    return Promise.reject(new Error("device_token and session_id are required"));
  }
  const categoryListForApi = sanitizeCategoryListForApi(category_list);

  if (!business_id) {
    return post<UpdateCategoryResponse>(api.CREATE_ALL_PLATFORM_CATEGORIES, {
      device_token,
      session_id,
      category_list: categoryListForApi,
    });
  }

  const businessIdNum = Number(business_id);
  return post<UpdateCategoryResponse>(api.CREATE_ALL_CATEGORIES, {
    business_id: Number.isNaN(businessIdNum) ? business_id : businessIdNum,
    device_token,
    session_id,
    category_list: categoryListForApi,
  });
}

function validateShortcode(payload: ValidateShortcodePayload): Promise<ValidateShortcodeResponse> {
  const { business_id, device_token, session_id, shortcode, entity_type } = payload;
  if (!device_token || !session_id) {
    return Promise.reject(new Error("device_token and session_id are required"));
  }
  if (!business_id) {
    return post<ValidateShortcodeResponse>(api.SHORTCODE_PLATFORM_VALIDATION, {
      shortcode: shortcode.trim(),
      entity_type,
      device_token,
      session_id,
    });
  }
  return post<ValidateShortcodeResponse>(api.SHORTCODE_VALIDATION, {
    shortcode: shortcode.trim(),
    entity_type,
    business_id,
    device_token,
    session_id,
  });
}

export const categoriesService = {
  getAllCategoryInfo,
  getPlatformCategories,
  applyPlatformTemplateNew,
  createCategory,
  updateCategory,
  makeCategoryLive,
  validateShortcode,
};
